import { useCallback, useState } from "react";
import { useMountedRef } from "../components/useMountedRef";

/**
 * Writes in flight, by key — so a second page's (or a second dialog's) button
 * isn't shown busy by the first one's call. useProductPages keys its page
 * actions `${kind}:${id}` ("request:151"); the section keys its confirmations
 * per item (`page:151`, `video:151:88`).
 *
 *   const { busy, markBusy } = useBusyKeys();
 *   if (busy[key]) return;
 *   markBusy(key, true);
 *   try { await call(); } finally { markBusy(key, false); }
 *
 * `markBusy` does nothing once the component has unmounted: the call it ends
 * can land after the page is left.
 */

/** A new map with `key` set (`on`) or dropped; `prev` is left as it is. */
export const withBusyKey = (prev: Record<string, true>, key: string, on: boolean): Record<string, true> => {
  if (on) return { ...prev, [key]: true };
  const next = { ...prev };
  delete next[key];
  return next;
};

export const useBusyKeys = () => {
  const mounted = useMountedRef();
  const [busy, setBusy] = useState<Record<string, true>>({});
  const markBusy = useCallback(
    (key: string, on: boolean) => {
      if (!mounted.current) return;
      setBusy((prev) => withBusyKey(prev, key, on));
    },
    [mounted],
  );
  return { busy, markBusy };
};
